import React from 'react';
import { isJumpingEvent } from './event';
import AthleteCategoryBadge from 'components/admin/result/AthleteCategoryBadge';
import { runnerNamesLine } from 'utils/relayTeams';

// Event-view name cell: keeps athleteName a plain string (so sort/search
// still work on the accessor) and appends the guest-class badge.
const renderAthleteNameCell = ({ value, row }) => (
  <>
    {value}
    <AthleteCategoryBadge category={row.athleteCategory} />
  </>
);

// Define separate options for different event types
export const throwAttemptOptions = ['OK', 'Foul', 'NR'];
export const jumpAttemptOptions = ['OK', 'Foul', 'NR'];
export const highResultAttemptOptions = ['.', '-', 'X', 'O'];

export const getAttemptOptionsForEvent = (eventType) => {
  if (eventType === 'High Jump') {
    return highResultAttemptOptions;
  }
  return isJumpingEvent(eventType) ? jumpAttemptOptions : throwAttemptOptions;
};

export const getResultAttemptCount = (results) => {
  let count = 0;
  if (results[0]?.attempts?.length) {
    for (let i = 0; i < results.length; i++) {
      if (count < results[i]?.attempts?.length) {
        count = results[i]?.attempts?.length;
      }
    }
    return count;
  }
  return 0;
};

export const getResultHighJumpHeights = (results) => {
  let heighArrays = new Set();
  for (let i = 0; i < results.length; i++) {
    for (let j = 0; j < results[i]?.heightAttempts?.length; j++) {
      heighArrays.add(results[i]?.heightAttempts[j]?.height);
    }
  }
  return [...heighArrays].sort((a, b) => a - b);
};

export const resultColumns = (results) => {
  // using for event view
  if (results[0]?.highJumpAttempts?.length) {
    return highJumpColumns(getResultHighJumpHeights(results).length);
  }
  if (results[0]?.attempts?.length) {
    return fieldEventColumns(getResultAttemptCount(results));
  }
  return trackEventColumns;
};

// Field events. Their results table has attempt or height columns that are
// built from the results themselves. The same names the server treats as
// field events (its catalogue's Field event types).
const FIELD_EVENT_TYPES = [
  'High Jump',
  'Long Jump',
  'Triple Jump',
  'Shot Put',
  'Discus',
  'Javelin',
  'Vortex',
];

// The columns for an event that has no results yet, so its page can show an
// empty table that looks like the one results will appear in. A timed event
// gets its full set. A field event gets the columns every kind of event
// shares (ID to Season Best), because its other columns depend on the results.
export const emptyResultColumns = (eventType) =>
  FIELD_EVENT_TYPES.includes(eventType)
    ? trackEventColumns.slice(0, 7)
    : trackEventColumns;

export const resultAttemptsCount = (results) => {
  if (results[0]?.highJumpAttempts?.length) {
    return results[0]?.highJumpAttempts?.length;
  }
  if (results[0]?.attempts?.length) {
    return getResultAttemptCount(results);
  }
  return 0;
};

export const trackEventColumns = [
  {
    accessor: 'athleteNumber',
    label: 'ID',
    headerStyle: {
      paddingLeft: 16,
      minWidth: 70,
      letterSpacing: -0.3,
    },
    style: {
      paddingLeft: 16,
      minWidth: 70,
    },
  },
  {
    accessor: 'athleteName',
    label: 'Name',
    Cell: renderAthleteNameCell,
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      minWidth: 200,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
      minWidth: 200,
    },
  },
  {
    accessor: 'athleteGender',
    label: 'Gender',
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      minWidth: 70,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
      minWidth: 70,
    },
  },
  {
    accessor: 'athleteAge',
    label: 'Age',
    groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      minWidth: 70,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
      minWidth: 70,
    },
  },
  {
    accessor: 'position',
    label: 'Position',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'result',
    label: 'Result',
    checkbox: false,
    sortable: false,
    hasStar: true,
    starAccessor: 'isPR',
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'seasonBestValue',
    label: 'Season Best',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'status',
    label: 'Status',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'laneNumber',
    label: 'Lane',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
];

export const ageViewTrackEventColumns = [
  {
    accessor: 'athleteNumber',
    label: 'ID',
    headerStyle: {
      paddingLeft: 16,
      minWidth: 70,
      letterSpacing: -0.3,
    },
    style: {
      paddingLeft: 16,
      minWidth: 70,
    },
  },
  {
    accessor: 'athleteName',
    label: 'Name',
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
    },
  },
  {
    accessor: 'athleteGender',
    label: 'Gender',
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
    },
  },
  {
    accessor: 'athleteAge',
    label: 'Age',
    groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
    },
  },
  {
    accessor: 'position',
    label: 'Position',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'result',
    label: 'Result',
    checkbox: false,
    sortable: false,
    hasStar: true,
    starAccessor: 'isPR',
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },
  {
    accessor: 'seasonBestValue',
    label: 'Season Best',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      paddingLeft: 22,
    },
  },
  {
    accessor: 'status',
    label: 'Status',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      paddingLeft: 22,
    },
  },
  {
    accessor: 'button',
    label: 'Event',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      paddingLeft: 22,
    },
  },
];

export const fieldEventColumns = (attemptNumber) => {
  return [
    {
      accessor: 'athleteNumber',
      label: 'ID',
      headerStyle: {
        paddingLeft: 16,
        minWidth: 90,
        letterSpacing: -0.3,
      },
      style: {
        paddingLeft: 16,
        minWidth: 90,
      },
    },
    {
      accessor: 'athleteName',
      label: 'Name',
      Cell: renderAthleteNameCell,
      headerStyle: {
        paddingLeft: 20,
        minWidth: 200,
        justifyContent: 'space-between',
      },
      style: {
        minWidth: 200,
        paddingLeft: 20,
      },
    },
    {
      accessor: 'athleteGender',
      label: 'Gender',
      headerStyle: {
        paddingLeft: 20,
        minWidth: 80,
        justifyContent: 'space-between',
      },
      style: {
        paddingLeft: 20,
        minWidth: 80,
      },
    },
    {
      accessor: 'athleteAge',
      label: 'Age',
      groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
      headerStyle: {
        paddingLeft: 20,
        minWidth: 80,
        justifyContent: 'space-between',
      },
      style: {
        paddingLeft: 20,
        minWidth: 80,
      },
    },
    {
      accessor: 'position',
      label: 'Position',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 22,
        minWidth: 58,
      },
      style: {
        paddingLeft: 22,
        minWidth: 58,
      },
    },
    {
      accessor: 'result',
      label: 'Result',
      checkbox: false,
      sortable: false,
      hasStar: true,
      starAccessor: 'isPR',
      headerStyle: {
        paddingLeft: 22,
        minWidth: 58,
      },
      style: {
        paddingLeft: 22,
        minWidth: 58,
      },
    },
    {
      accessor: 'seasonBestValue',
      label: 'Season Best',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 22,
        minWidth: 58,
      },
      style: {
        paddingLeft: 22,
        minWidth: 58,
      },
    },
    {
      accessor: 'attempts',
      label: '',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 15,
        paddingRight: 10,
        minWidth: 90,
      },
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(${attemptNumber}, 1fr)`,
        minWidth: 90 * attemptNumber,
      },
    },
  ];
};

export const highJumpColumns = (attemptNumber) => {
  return [
    {
      accessor: 'athleteNumber',
      label: 'ID',
      headerStyle: {
        paddingLeft: 21,
        minWidth: 90,
        letterSpacing: -0.3,
      },
      style: {
        paddingLeft: 21,
        minWidth: 90,
      },
    },
    {
      accessor: 'athleteName',
      label: 'Name',
      Cell: renderAthleteNameCell,
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 25,
        justifyContent: 'space-between',
        minWidth: 200,
      },
      style: {
        paddingLeft: 20,
        minWidth: 200,
      },
    },
    {
      accessor: 'gender',
      label: 'Gender',
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 25,
        justifyContent: 'space-between',
        minWidth: 88,
      },
      style: {
        paddingLeft: 20,
        minWidth: 88,
      },
    },
    {
      accessor: 'age',
      label: 'Age',
      groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 25,
        justifyContent: 'space-between',
        minWidth: 70,
      },
      style: {
        paddingLeft: 20,
        minWidth: 70,
      },
    },
    {
      accessor: 'position',
      label: 'Position',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 22,
        paddingRight: 5,
        minWidth: 80,
      },
      style: {
        paddingLeft: 22,
        minWidth: 80,
      },
    },
    {
      accessor: 'result',
      label: 'Result',
      checkbox: false,
      sortable: false,
      hasStar: true,
      starAccessor: 'isPR',
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 5,
        minWidth: 90,
      },
      style: {
        paddingLeft: 20,
        minWidth: 90,
      },
    },
    {
      accessor: 'seasonBestValue',
      label: 'Season Best',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 10,
        minWidth: 90,
      },
      style: {
        paddingLeft: 20,
        minWidth: 90,
      },
    },
    {
      accessor: 'highJumpAttempts',
      label: '',
      checkbox: false,
      sortable: false,
      headerStyle: {
        paddingLeft: 20,
        minWidth: 90,
      },
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(${attemptNumber}, 2fr)`,
        minWidth: 90 * attemptNumber,
      },
    },
  ];
};

export const ageViewHighJumpColumns = [
  {
    accessor: 'athleteNumber',
    label: 'ID',
    headerStyle: {
      paddingLeft: 21,
      minWidth: 90,
      letterSpacing: -0.3,
    },
    style: {
      paddingLeft: 21,
      minWidth: 90,
    },
  },
  {
    accessor: 'athleteName',
    label: 'Name',
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
      minWidth: 154,
    },
    style: {
      paddingLeft: 20,
      minWidth: 154,
    },
  },
  {
    accessor: 'gender',
    label: 'Gender',
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
      minWidth: 88,
    },
    style: {
      paddingLeft: 20,
      minWidth: 88,
    },
  },
  {
    accessor: 'age',
    label: 'Age',
    groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
      minWidth: 70,
    },
    style: {
      paddingLeft: 20,
      minWidth: 70,
    },
  },
  {
    accessor: 'position',
    label: 'Position',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 80,
    },
    style: {
      paddingLeft: 22,
      minWidth: 80,
    },
  },
  {
    accessor: 'result',
    label: 'Result',
    checkbox: false,
    sortable: false,
    hasStar: true,
    starAccessor: 'isPR',
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 5,
      minWidth: 90,
    },
    style: {
      paddingLeft: 22,
      minWidth: 90,
    },
  },
  {
    accessor: 'seasonBestValue',
    label: 'Season Best',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
      minWidth: 90,
    },
    style: {
      paddingLeft: 22,
      minWidth: 90,
    },
  },
  {
    accessor: 'competitionStatus',
    label: 'Status',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      paddingLeft: 22,
    },
  },
  {
    accessor: 'button',
    label: 'Event',
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      paddingLeft: 22,
    },
  },
];

export const editTrackEventColumns = [
  {
    accessor: 'athleteNumber',
    label: 'ID',
    editable: false,
    headerStyle: {
      paddingLeft: 20,
      minWidth: 80,
      letterSpacing: -0.3,
    },
    style: {
      paddingLeft: 20,
      minWidth: 80,
    },
  },
  {
    accessor: 'athleteName',
    label: 'Name',
    editable: false,
    newEditable: true,
    editType: 'searchSelect',
    showDeleteIcon: true, // Enable delete icon for this column existing athlete result can be deleted
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      minWidth: 154,
      justifyContent: 'space-between',
    },
    style: {
      paddingLeft: 20,
      minWidth: 154,
    },
  },
  {
    accessor: 'athleteGender',
    label: 'Gender',
    headerStyle: {
      minWidth: 58,
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
    },
    style: {
      minWidth: 58,
      paddingLeft: 20,
    },
  },
  {
    accessor: 'athleteAge',
    label: 'Age',
    groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
    headerStyle: {
      minWidth: 58,
      paddingLeft: 20,
      paddingRight: 25,
      justifyContent: 'space-between',
    },
    style: {
      minWidth: 58,
      paddingLeft: 20,
    },
  },
  {
    accessor: 'resultValue',
    label: 'Result (min:sec.ms)',
    checkbox: false,
    sortable: false,
    editable: true,
    editType: 'text',
    headerStyle: {
      paddingLeft: 10,
      paddingRight: 5,
      minWidth: 80,
    },
    style: {
      paddingLeft: 10,
      paddingRight: 10,
      minWidth: 80,
    },
  },
  {
    accessor: 'laneNumber',
    label: 'Lane',
    editable: true,
    editType: 'select',
    options: ['1', '2', '3', '4', '5', '6', '7', '8'],
    checkbox: false,
    sortable: false,
    headerStyle: {
      paddingLeft: 22,
      paddingRight: 10,
      minWidth: 58,
    },
    style: {
      paddingLeft: 22,
      minWidth: 58,
    },
  },

  {
    accessor: 'status',
    label: 'Status',
    checkbox: false,
    sortable: false,
    editable: true,
    editType: 'select',
    options: ['OK', 'DNF'],
    headerStyle: {
      minWidth: 80,
      paddingLeft: 22,
      paddingRight: 10,
    },
    style: {
      minWidth: 80,
      paddingLeft: 22,
    },
  },
];

export const editFieldEventColumns = (results, eventType) => {
  const columns = [
    {
      accessor: 'athleteNumber',
      label: 'ID',
      // editable: false,
      // newEditable: true,
      editType: 'text',
      headerStyle: {
        paddingLeft: 20,
        minWidth: 80,
        letterSpacing: -0.3,
      },
      style: {
        paddingLeft: 20,
        minWidth: 80,
      },
    },
    {
      accessor: 'athleteName',
      label: 'Name',
      editable: false,
      newEditable: true,
      editType: 'searchSelect',
      showDeleteIcon: true, // Enable delete icon for this column existing athlete result can be deleted
      headerStyle: {
        paddingLeft: 20,
        paddingRight: 25,
        minWidth: 200,
        justifyContent: 'space-between',
      },
      style: {
        paddingLeft: 20,
        minWidth: 200,
      },
    },
    // Gender and Age as in a track event's dialog. The high jump heights
    // after them are found by their names, not their places (AdminDataTable).
    {
      accessor: 'athleteGender',
      label: 'Gender',
      headerStyle: {
        minWidth: 58,
        paddingLeft: 20,
        paddingRight: 25,
        justifyContent: 'space-between',
      },
      style: {
        minWidth: 58,
        paddingLeft: 20,
      },
    },
    {
      accessor: 'athleteAge',
      label: 'Age',
      groupColumn: true, // one athlete's group: AdminDataTable heads and shows it in the club's terms
      headerStyle: {
        minWidth: 58,
        paddingLeft: 20,
        paddingRight: 25,
        justifyContent: 'space-between',
      },
      style: {
        minWidth: 58,
        paddingLeft: 20,
      },
    },
  ];
  const heighArrays = getResultHighJumpHeights(results);
  const options = getAttemptOptionsForEvent(eventType);
  if (eventType === 'High Jump') {
    for (let index = 0; index < heighArrays?.length; index++) {
      columns.push({
        accessor: `heightAttempts${index}`,
        label: `Height`,
        checkbox: false,
        sortable: false,
        editable: true,
        editType: 'highJumpAttempt',
        headerStyle: {
          paddingLeft: 22,
          paddingRight: 5,
          minWidth: 180,
        },
        style: {
          paddingLeft: 22,
          paddingRight: 10,
          minWidth: 180,
        },
        hasDeleteIcon:
          heighArrays?.length > 1 && index === heighArrays?.length - 1,
        options: options,
      });
    }
    return columns;
  }
  for (let index = 0; index < results[0]?.attempts?.length; index++) {
    columns.push({
      accessor: `attempt${index + 1}`,
      label: `Attempt ${index + 1}`,
      checkbox: false,
      sortable: false,
      editable: true,
      editType: 'attempt',
      headerStyle: {
        paddingLeft: 22,
        paddingRight: 5,
        minWidth: 180,
      },
      style: {
        paddingLeft: 22,
        paddingRight: 10,
        minWidth: 180,
      },
      hasDeleteIcon:
        results[0]?.attempts?.length > 1 &&
        index === results[0]?.attempts?.length - 1,
      options: options,
    });
  }
  return columns;
};

export const editColumnPrimaryField = 'resultID';

// ---- Relays run by teams (utils/relayTeams.js) ----

const cellPaddingColumn = (accessor, label, extra = {}) => ({
  accessor,
  label,
  checkbox: false,
  sortable: false,
  headerStyle: { paddingLeft: 22, paddingRight: 10, minWidth: 58 },
  style: { paddingLeft: 22, minWidth: 58 },
  ...extra,
});

// A relay team's name cell: the name, and its runners under it in leg order
// when it has them (step 3 file, section 13).
const renderTeamNameCell = ({ value, row }) => (
  <div>
    <div>
      {value}
      <AthleteCategoryBadge category={row.athleteCategory} />
    </div>
    {row.runners?.length > 0 && (
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
        {runnerNamesLine(row.runners)}
      </div>
    )}
  </div>
);

const teamColumn = (teamWord) => ({
  ...trackEventColumns.find((c) => c.accessor === 'athleteName'),
  label: teamWord,
  Cell: renderTeamNameCell,
});

// A relay's Event View, in a school and a club alike (Doug, 2026-10-16): in
// Doug's order of 2026-10-17, Position, the team (headed House, Faction or Team
// in a school, Team in a club), Result, Lane and Status. A team has no ID,
// gender, age or season best.
export const relayRaceColumns = ({ teamWord }) => [
  trackEventColumns.find((c) => c.accessor === 'position'),
  teamColumn(teamWord),
  trackEventColumns.find((c) => c.accessor === 'result'),
  cellPaddingColumn('laneNumber', 'Lane'),
  trackEventColumns.find((c) => c.accessor === 'status'),
];

// A relay round's Overall tab (utils/relayTeams.js), in Doug's order of
// 2026-10-17: each team's place across the round's races, the team, its result,
// the race and lane it ran in, and its status. A house's teams carry no letter:
// the race and lane tell two apart, as the officials know them at the start.
export const relayOverallColumns = ({ teamWord }) => [
  cellPaddingColumn('overallPosition', 'Overall'),
  teamColumn(teamWord),
  trackEventColumns.find((c) => c.accessor === 'result'),
  cellPaddingColumn('race', 'Race'),
  cellPaddingColumn('laneNumber', 'Lane'),
  trackEventColumns.find((c) => c.accessor === 'status'),
];

// The grid of the race page's relay table, one share for each column above.
export const relayRaceGrid = (columns) =>
  columns
    .map((c) => (c.accessor === 'athleteName' ? '5fr' : c.accessor === 'athleteNumber' ? '2fr' : '3fr'))
    .join(' ');

// The results dialog of a relay: the team, its time, lane and status. A new
// row's team cell is a choice of the houses and Other team in a school
// (teamChoices), and a box for the team's name in a club (no teamChoices). A
// saved team's cell carries its Runners button (runnersAction, a function of
// the row), before the delete icon.
export const editRelayEventColumns = ({ teamWord, teamChoices, runnersAction }) => [
  {
    accessor: 'athleteName',
    label: teamWord,
    editable: false,
    newEditable: true,
    editType: 'teamSelect',
    teamChoices,
    rowAction: runnersAction,
    showDeleteIcon: true,
    headerStyle: {
      paddingLeft: 20,
      paddingRight: 25,
      minWidth: 200,
      justifyContent: 'space-between',
    },
    style: { paddingLeft: 20, minWidth: 200 },
  },
  editTrackEventColumns.find((c) => c.accessor === 'resultValue'),
  editTrackEventColumns.find((c) => c.accessor === 'laneNumber'),
  editTrackEventColumns.find((c) => c.accessor === 'status'),
];
